import { useState } from 'react'
import { ModalForm, ProTable } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, DatePicker, Form, Input, InputNumber, Modal, Select, Space, Switch, Table } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount, formatAmountInput } from '@/utils/money.ts'
import { BILL_KINDS } from './const/bill-search.ts'
import { refreshMoneyQueries } from './utils/refresh-money-queries.ts'

dayjs.extend(utc)

type Values = {
  updatedAt?: Date
  autoGeneration?: boolean
  autoFrom?: Dayjs
  generationTiming?: 'start' | 'end'
  generationDay?: number | null
  generationOffsetDays?: number
  id?: number
  feeType: 'rent' | 'water' | 'electricity' | 'propertyManagement' | 'deposit' | 'other'
  chargeMode: 'once' | 'recurring' | 'metered'
  pricingUnit: 'fixed' | 'day' | 'month' | 'year' | 'unit'
  unitPrice?: string | null
  fixedAmount?: string | null
  cycleUnit?: 'day' | 'month' | 'year' | null
  cycleInterval?: number | null
  effectiveStart: Dayjs
  effectiveEnd?: Dayjs
  note?: string | null
}
export function RentalBillingPanel({ rentalOrderId, onBill }: { rentalOrderId: number; onBill: (id: number) => void }) {
  const trpc = useTRPC(),
    cache = useQueryClient(),
    { message } = App.useApp(),
    q = useQuery(trpc.bill.rentalRules.queryOptions({ rentalOrderId })),
    [editing, setEditing] = useState<Values>(),
    [showRules, setShowRules] = useState(false),
    [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month')]),
    [previewing, setPreviewing] = useState(false),
    [results, setResults] = useState<
      {
        ruleId: number
        feeType: string
        periodStart: string
        periodEnd: string
        amount: string | null
        autoScheduledDate: string | null
        billId: number | null
        status: string
      }[]
    >()
  const input = { rentalOrderId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') },
    preview = useQuery({ ...trpc.bill.previewRentalRange.queryOptions(input), enabled: previewing }),
    generate = useMutation(trpc.bill.generateRentalRange.mutationOptions())
  return (
    <>
      <Space wrap>
        <Button size="medium" onClick={() => setShowRules(true)}>
          收费规则设置
        </Button>
        <Button
          size="medium"
          onClick={() => {
            setResults(undefined)
            setPreviewing(true)
          }}
        >
          按规则生成草稿
        </Button>
      </Space>
      <Modal
        open={previewing}
        title="按收费规则生成草稿"
        width="65rem"
        onCancel={() => setPreviewing(false)}
        confirmLoading={generate.isPending}
        okText="生成草稿"
        okButtonProps={{ disabled: preview.isError || !preview.data?.length || preview.isFetching }}
        onOk={async () => {
          try {
            const rows = await generate.mutateAsync(input)
            await refreshMoneyQueries(cache, trpc)
            setResults(rows)
            await preview.refetch()
            const failed = rows.filter((r) => !r.success).length
            if (failed) {
              void message.warning(`${failed} 个账期失败，可按下方原因修正后重试；成功项已保留`)
            } else {
              void message.success(`已处理 ${rows.length} 个账期，重复账期复用原账单`)
            }
          } catch (e) {
            void message.error(e instanceof Error ? e.message : '生成失败')
          }
        }}
      >
        <p className="text-content-muted mb-4">
          根据已设置的费用、价格和周期，补齐所选期间缺少的账单草稿。已有账期会复用原账单，不会重复生成或重算金额；临时费用请使用“手动新建草稿”。
        </p>
        <Form.Item label="生成账期范围">
          <DatePicker.RangePicker
            presets={RANGE_PRESETS}
            aria-label="生成账期范围"
            value={range}
            allowClear={false}
            disabled={generate.isPending}
            onChange={(v) => {
              if (v?.[0] && v[1]) {
                setRange([v[0], v[1]])
                setResults(undefined)
              }
            }}
          />
        </Form.Item>
        {preview.isError && <Alert type="error" title={preview.error.message} />}
        <Alert
          type="info"
          className="mb-3"
          title="租金按订单约定金额生成，首尾不足周期请在出账前核对；水电需录入用量核费，确定免费请填 0。"
        />
        <Table
          rowKey={(r) => `${r.ruleId}:${r.periodStart}`}
          loading={preview.isFetching}
          pagination={false}
          dataSource={results ?? preview.data ?? []}
          columns={[
            { title: '费用', render: (_, r) => BILL_KINDS[r.feeType as keyof typeof BILL_KINDS] },
            { title: '开始', dataIndex: 'periodStart' },
            { title: '结束（含）', dataIndex: 'periodEnd' },
            { title: '金额', render: (_, r) => (r.amount === null ? '待人工确认' : formatAmount(r.amount)) },
            { title: '自动生成日', render: (_, r) => r.autoScheduledDate ?? '未启用' },
            { title: '结果', dataIndex: 'status' },
            {
              title: '账单',
              render: (_, r) =>
                r.billId && (
                  <Button type="link" onClick={() => onBill(r.billId!)}>
                    #{r.billId}
                  </Button>
                )
            }
          ]}
        />
      </Modal>
      <Modal open={showRules} title="收费规则设置" width="65rem" onCancel={() => setShowRules(false)} footer={null}>
        <p className="text-content-muted mb-4">
          设置本订单收什么费用、每期收多少、多久收一次。保存规则不会立即新增或重算账单；可再点击“按规则生成草稿”，或开启自动生成草稿。
        </p>
        {q.isError && <Alert type="error" title={q.error.message} />}
        <ProTable
          rowKey="id"
          search={false}
          pagination={false}
          columnsState={{ persistenceKey: 'finance:rules', persistenceType: 'localStorage' }}
          loading={q.isFetching}
          dataSource={q.data ?? []}
          options={{ reload: () => void q.refetch() }}
          toolBarRender={() => [
            <Button
              key="add"
              type="primary"
              onClick={() =>
                setEditing({
                  feeType: 'other',
                  chargeMode: 'recurring',
                  pricingUnit: 'month',
                  cycleUnit: 'month',
                  cycleInterval: 1,
                  effectiveStart: dayjs()
                })
              }
            >
              新增收费规则
            </Button>
          ]}
          columns={[
            { title: 'ID', dataIndex: 'id', copyable: true },
            { title: '费用', render: (_, r) => BILL_KINDS[r.feeType] },
            { title: '单价', dataIndex: 'unitPrice', renderText: formatAmount },
            { title: '固定金额', dataIndex: 'fixedAmount', renderText: formatAmount },
            {
              title: '自动生成草稿',
              render: (_, r) =>
                r.autoGeneration && r.autoFrom ? `已开启 · ${dayjs.utc(r.autoFrom).format('YYYY-MM-DD')}` : '未开启'
            },
            { title: '起始', dataIndex: 'effectiveStart', valueType: 'date' },
            { title: '终止（不含）', dataIndex: 'effectiveEnd', valueType: 'date' },
            {
              fixed: 'right',
              align: 'center',
              title: '操作',
              valueType: 'option',
              render: (_, r) => (
                <Button
                  type="link"
                  onClick={() =>
                    setEditing({
                      ...r,
                      autoFrom: r.autoFrom ? dayjs(r.autoFrom) : undefined,
                      effectiveStart: dayjs(r.effectiveStart),
                      effectiveEnd: r.effectiveEnd ? dayjs(r.effectiveEnd) : undefined
                    })
                  }
                >
                  编辑
                </Button>
              )
            }
          ]}
        />
      </Modal>
      {editing && (
        <RuleEditor
          rentalOrderId={rentalOrderId}
          initial={editing}
          onClose={() => setEditing(undefined)}
          onSaved={async () => {
            await q.refetch()
            setEditing(undefined)
          }}
        />
      )}
    </>
  )
}
function RuleEditor({
  rentalOrderId,
  initial,
  onClose,
  onSaved
}: {
  rentalOrderId: number
  initial: Values
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC(),
    { message } = App.useApp(),
    save = useMutation(trpc.bill.saveRentalRule.mutationOptions())
  return (
    <ModalForm<Values>
      dateFormatter={false}
      open
      title="收费规则"
      layout="vertical"
      initialValues={initial}
      modalProps={{ onCancel: onClose, destroyOnHidden: true }}
      submitter={{ submitButtonProps: { loading: save.isPending } }}
      onFinish={async (v) => {
        try {
          await save.mutateAsync({
            ...v,
            id: initial.id,
            expectedUpdatedAt: initial.updatedAt,
            autoGeneration: v.autoGeneration ?? false,
            autoFrom: v.autoFrom?.format('YYYY-MM-DD') ?? null,
            generationTiming: v.generationTiming ?? 'end',
            generationDay: v.cycleUnit === 'month' ? (v.generationDay ?? null) : null,
            generationOffsetDays: v.generationOffsetDays ?? 0,
            rentalOrderId,
            effectiveStart: v.effectiveStart.format('YYYY-MM-DD'),
            effectiveEnd: v.effectiveEnd?.format('YYYY-MM-DD') ?? null,
            unitPrice: v.unitPrice ?? null,
            fixedAmount: v.fixedAmount ?? null,
            cycleUnit: v.chargeMode === 'once' ? null : (v.cycleUnit ?? null),
            cycleInterval: v.chargeMode === 'once' ? null : (v.cycleInterval ?? null),
            note: v.note ?? null
          })
          await onSaved()
          return true
        } catch (e) {
          void message.error(e instanceof Error ? e.message : '保存失败')
          return false
        }
      }}
    >
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="feeType" label="费用" rules={[{ required: true }]}>
          <Select
            disabled={!!initial.id}
            options={['rent', 'water', 'electricity', 'propertyManagement', 'deposit', 'other'].map((value) => ({
              value,
              label: BILL_KINDS[value as keyof typeof BILL_KINDS]
            }))}
          />
        </Form.Item>
        <Form.Item name="chargeMode" label="收费方式" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'once', label: '一次性' },
              { value: 'recurring', label: '周期收费' },
              { value: 'metered', label: '按用量待核' }
            ]}
          />
        </Form.Item>
        <Form.Item name="pricingUnit" label="计价单位" rules={[{ required: true }]}>
          <Select
            options={[
              { value: 'fixed', label: '固定金额' },
              { value: 'day', label: '天' },
              { value: 'month', label: '月' },
              { value: 'year', label: '年' },
              { value: 'unit', label: '用量' }
            ]}
          />
        </Form.Item>
        <Form.Item name="unitPrice" label="单价">
          <InputNumber stringMode precision={2} formatter={formatAmountInput} min="0" />
        </Form.Item>
        <Form.Item name="fixedAmount" label="固定金额（与单价互斥）">
          <InputNumber stringMode precision={2} formatter={formatAmountInput} min="0" />
        </Form.Item>
        <Form.Item name="cycleUnit" label="周期单位">
          <Select
            allowClear
            options={[
              { value: 'day', label: '天' },
              { value: 'month', label: '自然月' },
              { value: 'year', label: '年' }
            ]}
          />
        </Form.Item>
        <Form.Item name="cycleInterval" label="每几个周期">
          <InputNumber min={1} precision={0} />
        </Form.Item>
        <Form.Item name="effectiveStart" label="生效开始" rules={[{ required: true }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="effectiveEnd" label="生效终止（不含）">
          <DatePicker />
        </Form.Item>
      </div>
      <Alert
        className="mb-4"
        type="info"
        title="自动生成草稿，实际收费仍须核定"
        description="先填写起算日期控制补生范围。月度后收模式在周期结束后的月份指定日生成；日租、年租可按周期开始或结束生成。"
      />
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item name="autoGeneration" label="自动生成草稿" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item name="autoFrom" label="自动起算日期">
          <DatePicker />
        </Form.Item>
        <Form.Item name="generationTiming" label="生成时点">
          <Select
            options={[
              { value: 'start', label: '周期开始' },
              { value: 'end', label: '周期结束后' }
            ]}
          />
        </Form.Item>
        <Form.Item name="generationDay" label="月周期生成日">
          <InputNumber min={1} max={28} precision={0} placeholder="留空按周期边界" />
        </Form.Item>
        <Form.Item name="generationOffsetDays" label="延后天数">
          <InputNumber min={0} max={365} precision={0} />
        </Form.Item>
      </div>
      <Form.Item name="note" label="备注">
        <Input.TextArea maxLength={5000} />
      </Form.Item>
    </ModalForm>
  )
}
